import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as LoadingIcons from "react-loading-icons";
import { useSelector } from "react-redux";
import { selectAuthComptable } from "../Redux/authComptableSlice"; // ✅ selector comptable
import { infoComptable, getStatistics, tvaMensuelle } from "../services/LoginService";
import CookieConsent from "./CookieConsent";
import DateRangeInput from "./DateRangeInput";
import { startOfMonth } from "date-fns";
import { saveAs } from "file-saver";

const DashboardComptable = () => {
  const { token } = useSelector(selectAuthComptable); // ✅ token comptable
  const [user, setUser] = useState(null);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [dateRangeTVA, setDateRangeTVA] = useState({
    startDate: startOfMonth(new Date()),
    endDate: new Date(),
  });

  const [dateRange, setDateRange] = useState({
    startDate: startOfMonth(new Date()),
    endDate: new Date(),
  });

  const [tvaParTaux, setTvaParTaux] = useState({});
  const [tvaUser, setTvaUser] = useState({});
  const [totauxTVA, setTotauxTVA] = useState(0);

  const formatEuro = (value) => {
    if (value == null || isNaN(value)) return "0 €";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
    }).format(value);
  };

  // ✅ Récupérer infos user
  const fetchUserInfo = async () => {
    try {
      const res = await infoComptable(token);
      if (res.success) {
        setUser(res.user);
      } else {
        toast.error(res.message || "Impossible de récupérer les infos du comptable");
      }
    } catch (err) {
      toast.error(err.message || "Erreur serveur");
    }
  };

  // ✅ Stats
  const fetchStats = async (start, end) => {
    setLoading(true);
    try {
      const res = await getStatistics(token, start, end);
      if (res.success) {
        setStats(res);
      } else {
        toast.error(res.message || "Erreur lors de la récupération des statistiques");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchUserInfo();
      fetchStats(dateRange.startDate, dateRange.endDate);
    }
  }, [token, dateRange.startDate, dateRange.endDate]);

  // ✅ TVA
  const fetchTVA = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await tvaMensuelle(token, dateRangeTVA.startDate, dateRangeTVA.endDate);
      if (result.success) {
        setTvaUser(result.tva_user || {});
        setTvaParTaux(result.tva_par_taux || {});
        setTotauxTVA(result.totaux_tva || 0);
      } else {
        setTvaUser({});
        setTvaParTaux({});
        setTotauxTVA(0);
      }
    } catch (err) {
      console.error("Erreur fetch TVA:", err);
      setTvaUser({});
      setTvaParTaux({});
      setTotauxTVA(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTVA();
  }, [dateRangeTVA.startDate, dateRangeTVA.endDate, token]);

  // ✅ Handlers manquants
  const handleDateChange = ({ startDate, endDate }) => {
    setDateRange({ startDate, endDate });
  };

  const handleDateChangeTVA = ({ startDate, endDate }) => {
    setDateRangeTVA({ startDate, endDate });
  };

  const handleExportTva = async () => {
    if (!dateRangeTVA.startDate || !dateRangeTVA.endDate) {
      toast.error("Veuillez choisir une période avant d’exporter");
      return;
    }

    setLoading(true);

    const formatDate = (d) => {
      const date = new Date(d);
      return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
    };

    const dateDebut = formatDate(dateRangeTVA.startDate);
    const dateFin = formatDate(dateRangeTVA.endDate);

    const tvaDetails = {};
    [5.5, 10, 20].forEach((taux) => {
      if (tvaUser[`${taux}%`]) {
        tvaDetails[taux] = tvaParTaux[`${taux}%`] ?? 0;
      }
    });

    const payload = { date_debut: dateDebut, date_fin: dateFin, tva_details: tvaDetails };

    try {
      const response = await fetch(`http://localhost:8000/api/v1/tva/export-excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Erreur lors de l'export");

      const blob = await response.blob();
      saveAs(blob, `TVA_${dateDebut}_to_${dateFin}.xlsx`);
    } catch (err) {
      toast.error("Impossible d’exporter le fichier Excel");
    } finally {
      setLoading(false);
    }
  };

  const handleExportTvaToComptable = async () => {
    if (!dateRangeTVA.startDate || !dateRangeTVA.endDate) {
      toast.error("Veuillez choisir une période avant d’exporter");
      return;
    }

    setLoading(true);

    const formatDate = (d) => {
      const date = new Date(d);
      return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
    };

    const dateDebut = formatDate(dateRangeTVA.startDate);
    const dateFin = formatDate(dateRangeTVA.endDate);

    const tvaDetails = {};
    [5.5, 10, 20].forEach((taux) => {
      if (tvaUser[`${taux}%`]) {
        tvaDetails[taux] = tvaParTaux[`${taux}%`] ?? 0;
      }
    });

    const payload = { date_debut: dateDebut, date_fin: dateFin, tva_details: tvaDetails };

    try {
      const response = await fetch(`http://localhost:8000/api/v1/tva/export-excel/parmail`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Erreur TVA");

      toast.success(data.message || "Fichier TVA envoyé au comptable !");
    } catch (err) {
      toast.error(err.message || "Erreur inattendue");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="flex flex-col lg:gap-2 gap-2 pb-4 pt-1 relative lg:px-0 px-4">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Loading général */}
      {/* ✅ Header Bienvenue */}
      {user && (
        <div className="w-full bg-white text-color1 py-5 px-6 rounded-xl border border-gray-200 shadow-sm">
          <h1 className="lg:text-2xl text-lg font-semibold text-gray-800">
            Bienvenue dans l’espace statistique du client :
          </h1>
          <p className="mt-2 text-xl font-bold text-blue-700">{user.nom_complet}</p>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>
      )}

      {!loading && (<CookieConsent />)}
      {/* Loading général */}
      {loading && (
        <div className="fixed inset-0 flex justify-center items-center z-[10] bg-white bg-opacity-85">
          <div className="flex flex-col items-center">
            <LoadingIcons.SpinningCircles
              stroke="#1A2A6C"
              fill="#1A2A6C"
              strokeWidth={3}
              height={50}
              width={50}
            />
            <span className="mt-3 text-gray-800 font-medium">
              Chargement...
            </span>
          </div>
        </div>
      )}
      <div className="flex  flex-col   items-start text-start justify-start lg:gap-5 gap-1   py-6 px-4  rounded-2xl w-full">
        <DateRangeInput
          onChange={handleDateChange}
          title={"Statistique"}
          initialStartDate={dateRange.startDate}   // ✅ date initiale
          initialEndDate={dateRange.endDate}       // ✅ date initiale
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-3 gap-2 w-full mt-4">
          {/* Bloc Factures du mois */}
          <div className="flex flex-col gap-3 bg-blue-100 p-6 rounded-2xl shadow-lg w-full items-start">
            <div className="flex items-center gap-3">
              <div className="bg-blue-200 p-4 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6h6v6h5v2H4v-2h5z" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-blue-800">Factures du mois</p>
            </div>
            {stats && stats.statistiques && (
              <div className="flex flex-col items-start gap-1">
                <p className="lg:text-base text-sm text-blue-700 font-semibold">
                  Total HT : {formatEuro(stats.statistiques.factures_du_mois.total_ht_centimes)}
                </p>
                {(stats.tva_inclus || stats.statistiques.factures_du_mois.total_ttc_centimes !== stats.statistiques.factures_du_mois.total_ht_centimes) && (
                  <p className="lg:text-base text-sm text-blue-700 font-semibold">
                    Total TTC : {formatEuro(stats.statistiques.factures_du_mois.total_ttc_centimes)}
                  </p>
                )}
                <p className="lg:text-base text-sm text-blue-700 font-semibold">
                  {stats.statistiques.factures_du_mois.nombre} facture(s)
                </p>
              </div>
            )}
          </div>

          {/* Bloc Factures payées */}
          <div className="flex flex-col gap-3 bg-green-100 p-6 rounded-2xl shadow-lg w-full items-start">
            <div className="flex items-center gap-3">
              <div className="bg-green-200 p-4 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-green-800">Factures payées</p>
            </div>
            {stats && stats.statistiques && (
              <div className="flex flex-col items-start gap-1">
                <p className="lg:text-base text-sm text-green-700 font-semibold">
                  Total HT : {formatEuro(stats.statistiques.factures_payees.total_ht_centimes)}
                </p>
                {(stats.tva_inclus || stats.statistiques.factures_payees.total_ttc_centimes !== stats.statistiques.factures_payees.total_ht_centimes) && (
                  <p className="lg:text-base text-sm text-green-700 font-semibold">
                    Total TTC : {formatEuro(stats.statistiques.factures_payees.total_ttc_centimes)}
                  </p>
                )}
                <p className="lg:text-base text-sm text-green-700 font-semibold">
                  {stats.statistiques.factures_payees.nombre} facture(s)
                </p>
              </div>
            )}
          </div>

          {/* Bloc Factures non payées */}
          <div className="flex flex-col gap-3 bg-red-100 p-6 rounded-2xl shadow-lg w-full items-start">
            <div className="flex items-center gap-3">
              <div className="bg-red-200 p-4 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-red-800">Factures non payées</p>
            </div>
            {stats && stats.statistiques && (
              <div className="flex flex-col items-start gap-1">
                <p className="lg:text-base text-sm text-red-700 font-semibold">
                  Total HT : {formatEuro(stats.statistiques.factures_non_payees.total_ht_centimes)}
                </p>
                {(stats.tva_inclus || stats.statistiques.factures_non_payees.total_ttc_centimes !== stats.statistiques.factures_non_payees.total_ht_centimes) && (
                  <p className="lg:text-base text-sm text-red-700 font-semibold">
                    Total TTC : {formatEuro(stats.statistiques.factures_non_payees.total_ttc_centimes)}
                  </p>
                )}
                <p className="lg:text-base text-sm text-red-700 font-semibold">
                  {stats.statistiques.factures_non_payees.nombre} facture(s)
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Bloc TVA complet : s'affiche seulement si un taux est actif */}
      {Object.values(tvaUser).some(actif => actif) && (
        <div className="flex flex-col items-start text-start justify-start lg:gap-5 gap-1   py-6 px-4 rounded-2xl w-full">
          {/* DateRange pour filtrer la TVA */}

          <div className="flex lg:flex-row flex-col gap-2 justify-between w-full">
            <DateRangeInput
              onChange={handleDateChangeTVA}
              title="TVA"
              initialStartDate={dateRangeTVA.startDate}   // ✅ date initiale
              initialEndDate={dateRangeTVA.endDate}       // ✅ date initiale
            />
            {/* Section Exporter Facture */}
            <div className="flex flex-row  items-center justify-center gap-1 ">

              <p
                onClick={handleExportTvaToComptable}
                className="bg-color23 rounded-full w-auto items-center justify-center flex text-white px-4 py-2   cursor-pointer transition whitespace-nowrap text-center"
              >
                Envoyer au comptable
              </p>

              <p
                onClick={handleExportTva}
                className="bg-color1 rounded-full w-auto items-center justify-center flex text-white px-4 py-2   cursor-pointer transition whitespace-nowrap text-center"
              >
                Télécharger Excel
              </p>
            </div>

          </div>

          {/* Conteneur des taux TVA */}
          <div className="col-span-1 lg:col-span-3 bg-white w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-1">
              {Object.entries(tvaUser)
                .filter(([_, actif]) => actif) // seuls les taux actifs
                .map(([taux]) => {
                  const valeur = tvaParTaux[taux] ?? 0;

                  return (
                    <div
                      key={taux}
                      className="bg-white p-4 rounded-xl flex flex-col items-center justify-center shadow"
                    >
                      <p className="font-semibold text-gray-800">TVA {taux}</p>
                      <p className="font-bold text-gray-800">{valeur.toFixed(2)} €</p>
                    </div>
                  );
                })}

              {/* Bloc Total TVA */}
              <div className="bg-white p-4 rounded-xl flex flex-col items-center justify-center shadow">
                <p className="font-semibold">Total TVA</p>
                <p className="font-bold">{totauxTVA.toFixed(2)} €</p>
              </div>
            </div>

          </div>
        </div>
      )}







    </div>
  );
};

export default DashboardComptable;
